import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  effect,
  inject,
} from "@angular/core";
import { Subscription } from "rxjs";
import { SharedMaterialModule } from "@config/shared-material.module";
import { CreateSetupDocumentComponent } from "@components/create-sign-stepper/create-setup-document/create-setup-document.component";
import { PdfViewEditComponent } from "@components/pdf-editor/pdf-view-edit/pdf-view-edit.component";
import { PdfThankYouComponent } from "@components/pdf-editor/pdf-thank-you/pdf-thank-you.component";
import { TableComponent } from "@components/layouts/table/table.component";
import { IAppToken } from "@modals/api.modal";
import { MatStepper } from "@angular/material/stepper";
import { MatCardModule } from "@angular/material/card";
import { TableService } from "@services/table.service";
import { PdfEditStateService, PdfEditSelectedAnnotation } from "@services/pdf-edit-state.service";
import { StepperSelectionEvent } from "@angular/cdk/stepper";
import { CreateFlowStateService, CreateWizardStep } from "@services/create-flow-state.service";
import { SignService } from "@services/sign.service";
import { ToastService } from "@services/toast.service";
import {
  EnumTableDynamicInputType,
  EnumSignerRoleType,
  FIELD_EDITOR_TOOLS,
  ITableActionRow,
  IUploadPDFUser,
  isFieldToolAllowedByActions,
  SIGNER_ALLOWED_ACTIONS_OPTION_LABELS,
  SIGNER_ALLOWED_ACTIONS_OPTIONS,
  DEFAULT_SIGNER_ALLOWED_ACTIONS,
  resolveSignerAllowedActionsFromImport,
  TFieldToolId,
} from "@modals/app.modal";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import readXlsxFile, { type Row } from "read-excel-file/browser";
import { saveAs } from "file-saver";
import { BulkSignerTemplateService } from "@services/bulk-signer-template.service";
import { SELF_SIGN_SIGNATURE_COLORS } from "@utils/signature-color.util";

const EMAIL_REGEX = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i;

@Component({
  selector: "app-bulk-sign-stepper",
  standalone: true,
  imports: [
    SharedMaterialModule,
    CreateSetupDocumentComponent,
    PdfViewEditComponent,
    PdfThankYouComponent,
    MatCardModule,
    TableComponent,
    TranslateModule,
  ],
  templateUrl: "./bulk-sign-stepper.component.html",
  styleUrl: "./bulk-sign-stepper.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class BulkSignStepperComponent implements OnInit, AfterViewInit, OnDestroy {
  isPdfUploaded = false;
  pdfFile: File | null = null;
  readonly bulkStepLabelKeys = [
    "bulkStepImportSigners",
    "bulkStepSetupDocument",
    "bulkStepReviewDocument",
    "bulkStepSent",
  ] as const;
  topPosition = 0;
  appToken!: IAppToken;
  isEditable = true;
  excelFileName = "";
  excelImported = false;
  isDownloadingTemplate = false;
  wizardSetupPhase: 0 | 1 = 0;

  @ViewChild("stepContentContainer") stepContentContainer!: ElementRef;
  @ViewChild("setupDocRef") setupDocRef!: CreateSetupDocumentComponent;
  @ViewChild(PdfViewEditComponent) pdfViewEditComponent!: PdfViewEditComponent;
  @ViewChild("stepper") stepper!: MatStepper;
  @ViewChild("excelInput") excelInput!: ElementRef<HTMLInputElement>;

  pdfEditState = inject(PdfEditStateService);
  private _createFlow = inject(CreateFlowStateService);
  private _cdr = inject(ChangeDetectorRef);
  fontSizes = [8, 10, 12, 14, 16, 18, 20, 24];
  readonly selfSignSignatureColors = SELF_SIGN_SIGNATURE_COLORS;
  readonly fieldTools = FIELD_EDITOR_TOOLS;
  propertiesDrawerOpen = false;
  private _lastSelectedAnnEl: HTMLElement | null = null;
  private _pinnedPropertyAnnotation: PdfEditSelectedAnnotation | null = null;
  private _returnToDocumentUpload = false;
  private _tableDataSub?: Subscription;

  tableColumns: Array<{
    columnDef: string;
    header: string;
    isSortable?: boolean;
    required?: boolean;
    isEditable?: boolean;
    inputType?: EnumTableDynamicInputType;
    dropdownOptions?: string[];
    placeholder?: string;
    selectPlaceholder?: string;
    optionLabels?: Record<string, string>;
    readOnly?: boolean;
  }> = [
    { columnDef: "name", header: "name", isSortable: false, required: true, isEditable: true, inputType: EnumTableDynamicInputType.TEXT, placeholder: "name" },
    {
      columnDef: "role",
      header: "role",
      isSortable: false,
      required: true,
      isEditable: true,
      readOnly: true,
      inputType: EnumTableDynamicInputType.DROPDOWN,
      dropdownOptions: ["signer"],
      selectPlaceholder: "role",
      optionLabels: { signer: "roleSigner" },
    },
    { columnDef: "email", header: "email", isSortable: false, required: true, isEditable: true, inputType: EnumTableDynamicInputType.EMAIL, placeholder: "email" },
    { columnDef: "emailCc", header: "emailCc", isSortable: false, required: false, isEditable: true, inputType: EnumTableDynamicInputType.EMAIL, placeholder: "emailCc" },
    { columnDef: "phone", header: "phone", isSortable: false, required: false, isEditable: true, inputType: EnumTableDynamicInputType.TEL, placeholder: "phone" },
    {
      columnDef: "allowedActions",
      header: "allowedActions",
      isSortable: false,
      required: false,
      isEditable: true,
      inputType: EnumTableDynamicInputType.MULTI_DROPDOWN,
      dropdownOptions: [...SIGNER_ALLOWED_ACTIONS_OPTIONS],
      selectPlaceholder: "allowedActions",
      optionLabels: SIGNER_ALLOWED_ACTIONS_OPTION_LABELS,
    },
  ];

  tableRowActions: ITableActionRow[] = [
    {
      icon: "delete",
      color: "warn",
      title: "delete",
      isDisabled: (): boolean => !(this.tableService.tableInputData?.length > 1),
      actionFn: (row: IUploadPDFUser) => {
        const i = this.tableService.tableInputData.findIndex((d) => d.tempID === row.tempID);
        if (i >= 0) {
          this.tableService.tableInputData.splice(i, 1);
          this.tableService.syncSignerOrders();
          this.tableService.tableInputData = [...this.tableService.tableInputData];
        }
      },
    },
  ];

  constructor(
    public tableService: TableService,
    private _signService: SignService,
    private _toast: ToastService,
    private _translate: TranslateService,
    private _bulkSignerTemplate: BulkSignerTemplateService
  ) {
    effect(() => {
      const ann = this.pdfEditState.selectedAnnotation();
      if (!ann) {
        this._lastSelectedAnnEl = null;
        if (
          this._pinnedPropertyAnnotation &&
          !this._pinnedPropertyAnnotation.element.isConnected
        ) {
          this._pinnedPropertyAnnotation = null;
          this.propertiesDrawerOpen = false;
          this.pdfEditState.setPropertyDrawerOpen(false);
        } else if (!this.propertiesDrawerOpen) {
          this._pinnedPropertyAnnotation = null;
        }
        return;
      }
      if (ann.element !== this._lastSelectedAnnEl) {
        this._lastSelectedAnnEl = ann.element;
        this._pinnedPropertyAnnotation = ann;
        this.propertiesDrawerOpen = true;
        this.pdfEditState.setPropertyDrawerOpen(true);
        this.pdfEditState.schedulePropertyPanelScroll();
      } else {
        this._pinnedPropertyAnnotation = ann;
      }
    });
  }

  isToolDisabled(tool: { id: TFieldToolId; requiresSigner: boolean }): boolean {
    if (tool.requiresSigner && this.isAllReviewers()) {
      return true;
    }
    return !isFieldToolAllowedByActions(tool.id, this.tableService.tableInputData);
  }

  private isAllReviewers(): boolean {
    return (
      this.tableService.tableInputData.length > 0 &&
      this.tableService.tableInputData.every(
        (data) =>
          (data.role ?? "").toString().trim().toLowerCase() ===
          EnumSignerRoleType.REVIEWER.toLowerCase()
      )
    );
  }

  private refreshReviewToolsState(): void {
    const selected = this.pdfEditState.selectedTool();
    if (selected) {
      const tool = this.fieldTools.find((item) => item.id === selected);
      if (tool && this.isToolDisabled(tool)) {
        this.pdfEditState.setSelectedTool(null);
      }
    }
    this._cdr.markForCheck();
  }

  get displayedPropertyAnnotation(): PdfEditSelectedAnnotation | null {
    const live = this.pdfEditState.selectedAnnotation();
    if (live) return live;
    if (this.propertiesDrawerOpen && this._pinnedPropertyAnnotation) {
      const el = this._pinnedPropertyAnnotation.element;
      if (el.isConnected) return this._pinnedPropertyAnnotation;
      this._pinnedPropertyAnnotation = null;
    }
    return null;
  }

  togglePropertiesDrawer(): void {
    this.propertiesDrawerOpen = !this.propertiesDrawerOpen;
    if (this.propertiesDrawerOpen) {
      const ann = this.pdfEditState.selectedAnnotation();
      if (ann) this._pinnedPropertyAnnotation = ann;
    } else {
      this._pinnedPropertyAnnotation = null;
    }
    this.pdfEditState.setPropertyDrawerOpen(this.propertiesDrawerOpen);
  }

  closePropertiesDrawer(): void {
    this.propertiesDrawerOpen = false;
    this._pinnedPropertyAnnotation = null;
    this.pdfEditState.setPropertyDrawerOpen(false);
  }

  ngOnInit(): void {
    this.tableService.replaceTableInputData([]);
    this.pdfEditState.setShowToolsInHeader(false);
    this._createFlow.setWizardFlow("bulk");
    this._tableDataSub = this.tableService.tableInputDataChanged$.subscribe(() => {
      if (this.stepper?.selectedIndex === 2) {
        this.refreshReviewToolsState();
      }
    });
    this._createFlow.setWizardStep(0);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.stepContentContainer) {
        this.topPosition = this.stepContentContainer.nativeElement.offsetTop;
      }
      this.syncHeaderStep();
    });
  }

  private syncHeaderStep(): void {
    const matIdx = this.stepper?.selectedIndex ?? 0;
    let step: CreateWizardStep = 0;
    if (matIdx === 0) {
      step = 0;
    } else if (matIdx === 1) {
      step = 1;
    } else if (matIdx === 2) {
      step = 2;
    } else if (matIdx >= 3) {
      step = 3;
    }
    this._createFlow.setWizardStep(step);
    this.updateFieldStepActions(matIdx);
  }

  private updateFieldStepActions(matIdx: number): void {
    if (matIdx === 2) {
      this._createFlow.registerFieldStepActions({
        back: () => this.previousStepFromField(),
        submit: () => this.submitData(),
      });
    } else {
      this._createFlow.registerFieldStepActions(null);
    }
  }

  onSetupAdvance(): void {
    this.wizardSetupPhase = 1;
  }

  onSetupGoBack(): void {
    this.wizardSetupPhase = 0;
  }

  triggerExcelPick(): void {
    this.excelInput?.nativeElement?.click();
  }

  async downloadSignerTemplate(): Promise<void> {
    if (this.isDownloadingTemplate) return;
    this.isDownloadingTemplate = true;
    try {
      const blob = await this._bulkSignerTemplate.buildTemplateBlob();
      saveAs(blob, "bulk-signer-template.xlsx");
    } catch {
      this._toast.showError(this._translate.instant("bulkToastTemplateDownloadFailed"));
    } finally {
      this.isDownloadingTemplate = false;
    }
  }

  onExcelSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls")) {
      this._toast.showWarning(this._translate.instant("bulkToastExcelOnly"));
      return;
    }
    this.parseExcelFile(file);
  }

  onExcelDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    const n = file.name.toLowerCase();
    if (!n.endsWith(".xlsx") && !n.endsWith(".xls")) {
      this._toast.showWarning(this._translate.instant("bulkToastExcelOnly"));
      return;
    }
    this.parseExcelFile(file);
  }

  allowExcelDrop(event: DragEvent): void {
    event.preventDefault();
  }

  private parseExcelFile(file: File): void {
    void readXlsxFile(file)
      .then((sheets) => {
        try {
          const rawRows: Row[] = sheets[0]?.data ?? [];
          if (!rawRows.length) {
            this._toast.showError(this._translate.instant("bulkToastSpreadsheetEmpty"));
            return;
          }
          const headerRow = rawRows[0];
          const headerKeys = headerRow.map((cell: Row[number], idx: number) => {
            const s = String(cell ?? "").trim();
            return s || `__empty_${idx}`;
          });

          const rows: Record<string, unknown>[] = [];
          for (let i = 1; i < rawRows.length; i++) {
            const r = rawRows[i];
            const obj: Record<string, unknown> = {};
            headerKeys.forEach((key, j) => {
              if (key.startsWith("__empty_")) return;
              const cell = r[j];
              if (cell == null || cell === "") {
                obj[key] = "";
              } else if (cell instanceof Date) {
                obj[key] = cell.toISOString();
              } else {
                obj[key] = String(cell);
              }
            });
            rows.push(obj);
          }

          if (!rows.length) {
            this._toast.showError(this._translate.instant("bulkToastNoDataRows"));
            return;
          }
          const emailKey = this.resolveEmailColumnKey(rows[0]);
          if (!emailKey) {
            this._toast.showError(this._translate.instant("bulkToastNoEmailColumn"));
            return;
          }
          const nameKey = this.resolveColumnKey(rows[0], ["name", "signer name", "full name", "signer"]);
          const emailCcKey = this.resolveColumnKey(rows[0], [
            "email cc",
            "emailcc",
            "cc email",
            "ccemail",
            "signer email cc",
            "signeremailcc",
          ]);
          const phoneKey = this.resolveColumnKey(rows[0], ["phone", "mobile", "tel", "contact"]);
          const allowedActionsKey = this.resolveColumnKey(rows[0], [
            "allowed actions",
            "allowedactions",
            "allowed_actions",
          ]);

          const seen = new Set<string>();
          const users: IUploadPDFUser[] = [];
          for (const row of rows) {
            const email = String(row[emailKey] ?? "")
              .trim()
              .toLowerCase();
            if (!email || !EMAIL_REGEX.test(email)) continue;
            if (seen.has(email)) continue;
            seen.add(email);
            const nameRaw = nameKey ? String(row[nameKey] ?? "").trim() : "";
            const name = nameRaw || email.split("@")[0] || this._translate.instant("bulkDefaultSignerName");
            const emailCc = emailCcKey
              ? String(row[emailCcKey] ?? "").trim().toLowerCase()
              : "";
            const phone = phoneKey ? String(row[phoneKey] ?? "").trim() : "";
            const allowedActionsRaw = allowedActionsKey
              ? String(row[allowedActionsKey] ?? "").trim()
              : "";
            const allowedActions = resolveSignerAllowedActionsFromImport(
              allowedActionsRaw || DEFAULT_SIGNER_ALLOWED_ACTIONS
            );
            users.push({
              name,
              email,
              emailCc,
              phone: phone ? this.tableService.formatPhoneForApi(phone) : "",
              role: "signer",
              allowedActions,
              tempID: this._signService.generateUUID(),
            });
          }
          if (!users.length) {
            this._toast.showError(this._translate.instant("bulkToastNoValidEmails"));
            return;
          }
          this.tableService.tableInputData = users;
          this.tableService.syncSignerOrders();
          this.tableService.tableInputData = [...this.tableService.tableInputData];
          this.excelFileName = file.name;
          this.excelImported = true;
          this._toast.showSuccess(
            this._translate.instant("bulkToastImportedSigners", { count: users.length })
          );
        } catch {
          this._toast.showError(this._translate.instant("bulkToastCouldNotReadExcel"));
        }
      })
      .catch(() => this._toast.showError(this._translate.instant("bulkToastCouldNotReadExcel")));
  }

  private normalizeHeader(h: string): string {
    return (h ?? "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  }

  private resolveEmailColumnKey(sample: Record<string, unknown>): string | null {
    const keys = Object.keys(sample);
    const exact = keys.find((k) => {
      const n = this.normalizeHeader(k);
      return n === "email" || n === "e-mail" || n === "e mail" || n === "signer email";
    });
    if (exact) return exact;
    const partial = keys.find((k) => {
      const n = this.normalizeHeader(k);
      return n.includes("email") || n === "mail";
    });
    return partial ?? null;
  }

  private resolveColumnKey(sample: Record<string, unknown>, candidates: string[]): string | null {
    const keys = Object.keys(sample);
    for (const c of candidates) {
      const found = keys.find((k) => this.normalizeHeader(k) === c);
      if (found) return found;
    }
    for (const c of candidates) {
      const found = keys.find((k) => this.normalizeHeader(k).includes(c));
      if (found) return found;
    }
    return null;
  }

  handlePdfUploaded(file: File): void {
    this.pdfFile = file;
    this.isPdfUploaded = true;
  }

  handleAppTokenUpdated(updatedAppToken: IAppToken): void {
    this.appToken = updatedAppToken;
  }

  resetStepper(): void {
    this._createFlow.resetWizardStep();
    location.reload();
  }

  private checkForAllValidUsers(): boolean {
    return this.tableService.areAllSignersValid();
  }

  get resolvedDocumentName(): string {
    return this.setupDocRef?.resolveDocumentName() ?? "";
  }

  nextFromImportStep(): void {
    if (!this.excelImported || !this.checkForAllValidUsers()) {
      this.tableService.triggerTouchInputs();
      this._toast.showWarning(this._translate.instant("bulkToastFixSignerFields"));
      return;
    }
    if (this.stepper.selected) this.stepper.selected.completed = true;
    this.wizardSetupPhase = 0;
    this.stepper.next();
    this.syncHeaderStep();
    this.pdfEditState.setShowToolsInHeader(false);
  }

  nextStep(): void {
    if (
      this.isPdfUploaded &&
      this.appToken &&
      this.checkForAllValidUsers() &&
      this.setupDocRef.requesterForm.valid
    ) {
      if (this.stepper.selected) this.stepper.selected.completed = true;
      this.stepper.next();
      this.syncHeaderStep();
      this.pdfEditState.setShowToolsInHeader(false);
      setTimeout(() => {
        this.pdfViewEditComponent?.ensurePdfRendered(false);
        this.refreshReviewToolsState();
      }, 100);
    }
  }

  onStepperSelectionChange(event: StepperSelectionEvent): void {
    const selectedIndex = event.selectedIndex;
    const prevIdx = event.previouslySelectedIndex;

    if (selectedIndex === 1 && prevIdx === 2) {
      this.wizardSetupPhase = 1;
    }
    if (selectedIndex === 1 && prevIdx === 0) {
      this.wizardSetupPhase = 0;
    }
    if (selectedIndex === 0 && prevIdx === 1) {
      if (this._returnToDocumentUpload) {
        this._returnToDocumentUpload = false;
        this.wizardSetupPhase = 0;
      } else {
        this.wizardSetupPhase = 1;
      }
    }

    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      this.syncHeaderStep();
      return;
    }

    if (selectedIndex === 2) {
      const canProceed =
        this.isPdfUploaded &&
        this.appToken &&
        this.checkForAllValidUsers() &&
        this.setupDocRef?.requesterForm?.valid;
      if (!canProceed) {
        setTimeout(() => {
          this.stepper.selectedIndex = prevIdx ?? 1;
          this.nextStep();
        });
        return;
      }
      this.pdfEditState.setShowToolsInHeader(false);
      this.pdfEditState.setSelectedTool(null);
      setTimeout(() => {
        this.pdfViewEditComponent?.ensurePdfRendered(false);
        this.refreshReviewToolsState();
      }, 0);
    } else {
      this.pdfEditState.setShowToolsInHeader(false);
      if (selectedIndex === 0 || selectedIndex === 1) {
        this.pdfEditState.setSelectedTool(null);
      }
    }

    this.syncHeaderStep();
  }

  previousStep(): void {
    this.previousStepFromField();
  }

  private previousStepFromField(): void {
    this.pdfEditState.setShowToolsInHeader(false);
    this.pdfEditState.setSelectedTool(null);

    if (this.stepper?.selectedIndex === 2) {
      this.wizardSetupPhase = 1;
      this._createFlow.setWizardStep(1);
    }

    this.stepper.previous();
    setTimeout(() => this.syncHeaderStep(), 0);
  }

  handleSubmitComplete(): void {
    this.isEditable = false;
    this.pdfEditState.setShowToolsInHeader(false);
    this.pdfEditState.setSelectedTool(null);
    if (this.stepper.selected) this.stepper.selected.completed = true;
    this.stepper.next();
    this.syncHeaderStep();
  }

  submitData(): void {
    let requesterDetails = this.setupDocRef.requesterForm.value;
    if (this.pdfViewEditComponent && requesterDetails) {
      this.pdfViewEditComponent.docName = this.resolvedDocumentName;
      requesterDetails = {
        ...requesterDetails,
        phone: this.tableService.getFormattedNumber(requesterDetails.phone, this.setupDocRef.countryCode),
      };
      this.pdfViewEditComponent.submit(requesterDetails);
    }
  }

  goToPrevious(): void {
    this.previousStepFromField();
  }

  ngOnDestroy(): void {
    this._tableDataSub?.unsubscribe();
    this.pdfEditState.setShowToolsInHeader(false);
    this.pdfEditState.setSelectedTool(null);
    this.pdfEditState.setPropertyDrawerOpen(false);
    this._createFlow.registerFieldStepActions(null);
    this._createFlow.resetWizardStep();
  }
}
